import { observer } from 'mobx-react-lite';
import { useState, useEffect, useContext } from 'react';
import { Link, Outlet } from 'react-router-dom';

import Stack from '@mui/joy/Stack';
import Divider from '@mui/joy/Divider';

import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Tooltip from '@mui/joy/Tooltip';
import Chip from '@mui/joy/Chip';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';

import useServiceWorker from 'hooks/useServiceWorker';
import useUserIdentity from 'hooks/useUserIdentity';
import useFun from 'hooks/useFun';
import { Identity } from 'stores/identity_store';

import { RecentsContext } from 'contexts/recents_context';

import Success from 'components/Success';
import LockedFeature from 'components/LockedFeature';
import IdentityAvatar from 'components/IdentityAvatar';
import { ColorSchemeSwitcherRadio } from 'components/UserPresenceIndicator';


import { useColorScheme } from '@mui/joy/styles';

import pluralize from 'utils/pluralize';
import getIcon from 'utils/getIcon';
import { forageGet } from 'utils/misc';
import { forageSet } from 'utils/misc';

const ColorSchemeSettings = () => {

    const {mode } = useColorScheme();
    const DarkMode = getIcon('DarkMode');
    const LightMode = getIcon('LightMode');
    const SystemMode = getIcon('SettingsBrightness');

    return (
        <Stack spacing={1}>
            <Typography level="title-md"
                endDecorator={mode === 'dark' ? <DarkMode sx={{color: "primary.300"}} /> : mode === 'light' ?
                    <LightMode sx={{color: "gold"}} /> :
                    <SystemMode  />}
            >
                Color Mode
            </Typography>
            <ColorSchemeSwitcherRadio  />
        </Stack>
    );
};



function FontSettings() {
    const {fontSize, setFontSize, DEFAULT_FONT_SIZE} = useFun();
    const FormatSizeIcon = getIcon('FormatSize');

    // a list of font sizes to choose from
    const fontSizes = [
        { name: '10px', value: '10px' },
        { name: '12px', value: '12px' },
        { name: '14px', value: '14px' },
        { name: '16px', value: '16px' },
        { name: '18px', value: '18px' },
        { name: '20px', value: '20px' },
        { name: '22px', value: '22px' },
    ];

    function handleFontSizeChange(value: string) {
        setFontSize && setFontSize(value);
        document.documentElement.style.fontSize = value;
        Success(`Font size changed to ${value} for this device.`);
    }

    return (
        <Stack spacing={1}>
            <Typography
                endDecorator={<FormatSizeIcon />}
                level="title-md">
                Font Size
            </Typography>
            <Stack direction="row" spacing={1}>

            <Select
                onChange={(_e, value) => {
                    if(value) {
                        handleFontSizeChange(value as string);
                    }
                }}
                value={fontSize}>

                { fontSizes.map((font) => {
                    const name = font.name + (DEFAULT_FONT_SIZE === font.value ? ' (default)' : '');
                    return (
                        <Option
                            key={font.value}
                            value={font.value}>{name}</Option>
                    );
                })}
                </Select>

            </Stack>
        </Stack>
    );
}


const IdentitySwitcher = observer(() => {
    const { identities, currentIdentity, setCurrentIdentity } = useUserIdentity();
    const { clearRecents } = useContext(RecentsContext);
    const otherIdentities = identities.filter((id) => id.key != currentIdentity?.key);

    if(!currentIdentity) {
        return null;
    }
    if(identities.length < 2) {
        return null;
    }

    function updateIdentity(id: Identity) {
        clearRecents();
        setCurrentIdentity(id);
        Success("Switching identities...");
        setTimeout(() => {
            window.location.reload();
        }, 500);
        // navigate(`/identity/${id.key}`);
    }

    return (
        <Stack spacing={1}>
            <Typography level="body-sm">
                Click to switch Identity
            </Typography>
                { otherIdentities.map((id) =>
                    <Tooltip arrow placement="left" title="Switch to this identity">
                        <Button
                            onClick={() => updateIdentity(id)}
                            variant="plain"
                            color="primary"
                            size="sm">

                            <IdentityAvatar
                                interactive={false}
                                size="inline"
                                identityKey={id.key} />
                        </Button>
                    </Tooltip>
                ) }
        </Stack>
    );
});


export const SettingsContainer = () => {

    const SettingsIcon = getIcon('Settings');
    const AccountIcon = getIcon('ContactPageOutlined');
    return (

            <Stack m={2} >
                <Stack direction="row" spacing={2} mb={2}>
                    <Typography
                        startDecorator={<AccountIcon />}
                        level="title-lg">
                    <Link to={'/account'}>
                        Account
                    </Link>
                        </Typography>

                    <Typography
                        startDecorator={<SettingsIcon />}
                        level="title-lg">
                    <Link to={'/settings'}>
                        Settings
                    </Link>
                        </Typography>

                </Stack>
                <Outlet />
        </Stack>

    );
};

const Settings = () => {

    const [isDeleting, setIsDeleting] = useState(false);
    const { clearServiceWorker } = useServiceWorker();
    const { isSubscribed, askForPermission, unsupported } = useServiceWorker();
    const { currentIdentity } = useUserIdentity();
    const NotificationsIcon = getIcon('Notifications');

    const [playAudio, setPlayAudio] = useState(false);
    const SoundOffIcon = getIcon('VolumeMute');
    const SoundOnIcon = getIcon('VolumeUp');
    const Logout = getIcon('Logout');
    const Refresh = getIcon('Replay');

    useEffect(() => {
        document.title = "Settings";
        forageGet('play-sound').then((res) => {
            if (res === null || res === true) {
                console.log("Setting playAudio to true");
                setPlayAudio(true);
            }
            else {
                console.log("Setting playAudio to false");
                setPlayAudio(false);
            }

        });
    }, []);

    async function setSound(status: boolean) {
        return forageSet('play-sound', status)
            .then(() => {
                setPlayAudio(status);
                Success("Great, refreshing the page.");
                window.location.href = window.location.href;
            });
    }

    function turnOffSound() {
        setSound(false);
    }

    function turnOnSound() {
        setSound(true);
    }

    function doClearServiceWorker() {
        setIsDeleting(true);

        clearServiceWorker()
            .then((cnt: number) => {
                Success(pluralize(cnt, 'Service worker') + " deleted. Refresh to re-add it.");
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setIsDeleting(false);
            });
    }
    return (

        <Stack mb={5} spacing={3}>

            <Stack
                divider={<Divider />}
                spacing={3}>
                <Stack>
                    <Stack spacing={2}>
                        <Typography level="title-lg">
                            Appearance
                        </Typography>

                        <Stack
                            justifyContent="space-evenly"
                            spacing={3}>

                            <ColorSchemeSettings />
                            <FontSettings />

                        </Stack>
                    </Stack>
                </Stack>


                <Stack spacing={2}>
                <Typography level="title-lg">
                    Notifications
                </Typography>
                <Stack
                    spacing={3}>

                    <Stack spacing={2}>
                        <Typography level="title-md"
                        >
                            Sounds, Noises, and Pings
                        </Typography>

                        <Stack direction="row">
                            <LockedFeature
                                lockedIconName="VolumeUp"
                                unlockedIconName="VolumeMute"
                                copy="Change your audio settings"
                            >
                                { playAudio &&
                                    <Stack
                                        alignItems="center">
                                        <Typography level="title-md">Turn off audible pings</Typography>
                                        <Typography level="body-xs">This is the "ding" that you hear when a message arrives.</Typography>

                                        <Button
                                            onClick={() => turnOffSound()}
                                            variant="plain"
                                            color="primary"
                                            size="sm"
                                            endDecorator={<SoundOffIcon />}
                                        >
                                            Turn Off
                                        </Button>

                                    </Stack>
                                }


                                { !playAudio &&
                                    <Stack
                                    >
                                        <Typography level="title-md">Turn on audible pings</Typography>
                                        <Typography level="body-xs">This is the "ding" that you hear when a message arrives.</Typography>

                                        <Button
                                            onClick={() => turnOnSound()}
                                            variant="plain"
                                            color="primary"
                                            size="sm"
                                            endDecorator={<SoundOnIcon />}
                                        >
                                            Turn On
                                        </Button>

                                    </Stack>
                                }
                            </LockedFeature>

                        </Stack>
                    </Stack>
                                        </Stack>


                    <Stack spacing={1}>
                        <Typography level="title-md"
                        >
                            Push Notifications
                        </Typography>
                        <Stack direction="row" alignItems="center">
                            <LockedFeature
                                lockedIconName="Notifications"
                                unlockedIconName="NotificationsNone"
                                copy="Push Notifications"
                            >
                                <Typography level="title-md">Browser Notifications</Typography>
                                <Typography level="body-xs">Messages that arrive when you've left our website.</Typography>
                                {!isSubscribed() && currentIdentity?.key &&
                                    <Stack alignItems="center" direction="row">
                                        {unsupported ?
                                            <Tooltip arrow
                                                title="We don't support offline notifications in this browser. We're working on it.">
                                                <Chip
                                                    sx={{ cursor: "default" }}
                                                    color="danger"
                                                    startDecorator={<NotificationsIcon />}
                                                >Notifications not supported</Chip>
                                            </Tooltip> :
                                            <Button
                                                size="sm"
                                                variant="plain"
                                                color="danger"
                                                onClick={() => askForPermission()}>Enable Notifications</Button>}
                                    </Stack>
                                }
                                {isSubscribed() &&
                                    <Stack alignItems="center" direction="row">
                                        <Chip
                                            color="success"
                                            startDecorator={<NotificationsIcon />}
                                        >Notifications enabled</Chip>
                                    </Stack>
                                }

                            </LockedFeature>
                        </Stack>
                    </Stack>


                </Stack>


                <Stack>

                    <Typography level="title-lg"
                    >
                        Privacy
                    </Typography>

                <Stack
                   >

                    <IdentitySwitcher />

                    <Stack>

                    <Typography level="title-md"
                    >
                        Session
                    </Typography>
                        <Stack direction="row" >
                        <LockedFeature
                            copy="Log out"
                            lockedIconName="Logout"

                        >

                            <Link to="/logout">
                                <Typography
                                    startDecorator={<Logout />}
                                    color="danger"
                                    level="title-md">
                                    Confirm Logout</Typography>

                            </Link>

                        </LockedFeature>
                    </Stack>
                    </Stack>
                </Stack>
                </Stack>

                <Stack>
                <Typography level="body-md">This is mostly nit-picky stuff</Typography>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                   >

                    <LockedFeature
                        copy="Clear the service worker"
                    >
                        <Stack>
                            <Typography level="title-md">Delete the Service Worker</Typography>
                            <Typography level="body-xs">Won't hurt anything, but probably won't fix any issues you're having.</Typography>

                            <Tooltip arrow
                                title="This will delete the service worker.">
                                <Button
                                    onClick={() => doClearServiceWorker()}
                                    variant="plain"
                                    color="primary"
                                    loading={isDeleting}
                                    size="sm"
                                >
                                    Delete
                                </Button>
                            </Tooltip>

                        </Stack>

                    </LockedFeature>


                    <LockedFeature
                        copy="Refresh the app"
                    >
                        <Stack
                        >
                            <Typography level="title-md">Refresh this page?</Typography>

                                <Button
                                    onClick={() => window.location.reload()}
                                    variant="plain"
                                    color="primary"
                                    size="sm"
                                    endDecorator={<Refresh />}
                                >
                                    Refresh
                                </Button>


                        </Stack>

                    </LockedFeature>


                </Stack>
                </Stack>
            </Stack>



        </Stack>

    );

};



export default Settings;
